import React, { useCallback, useContext, useMemo } from 'react';
import { DEFAULT_THEME } from '../constants';
import DisabledItem from './DisabledItem';
import GroupItem from './GroupItem';
import Item from './Item';
import { SelectContext } from './SelectProvider';

const Options = ({
  id,
  list,
  noOptionsMessage,
  text,
  isMultiple,
  value,
  primaryColor = DEFAULT_THEME,
}) => {
  const { classNames } = useContext(SelectContext);

  const filterByText = useCallback(() => {
    const isMatch = (str) => str.toLowerCase().includes(text.toLowerCase());

    return list.reduce((acc, item) => {
      if ('options' in item) {
        const groupLabelMatches = isMatch(item.label);

        const filteredChildren = groupLabelMatches
          ? item.options
          : item.options.filter((child) => isMatch(child.label));

        if (filteredChildren.length > 0) {
          acc.push({
            ...item,
            options: filteredChildren,
          });
        }
      } else {
        if (isMatch(item.label)) {
          acc.push(item);
        }
      }
      return acc;
    }, []);
  }, [text, list]);

  const removeValues = useCallback(
    (array) => {
      if (!isMultiple) {
        return array;
      }

      if (Array.isArray(value)) {
        const valueId = value.map((item) => item.value);

        const filterItem = (item) => !valueId.includes(item.value);

        let newArray = array.map((item) => {
          if ('options' in item) {
            return {
              label: item.label,
              options: item.options.filter(filterItem),
            };
          }
          return item;
        });

        newArray = newArray.filter((item) => {
          if ('options' in item) {
            return item.options.length > 0;
          } else {
            return filterItem(item);
          }
        });

        return newArray;
      }
      return array;
    },
    [isMultiple, value]
  );

  const filterResult = useMemo(() => {
    return removeValues(filterByText());
  }, [filterByText, removeValues]);

  return (
    <div
      id={id}
      role='listbox'
      className={classNames && classNames.list ? classNames.list : 'max-h-72 overflow-y-auto'}
    >
      {filterResult.map((item, index) => (
        <React.Fragment key={index}>
          {'options' in item ? (
            <>
              <div className='px-2.5'>
                <GroupItem primaryColor={primaryColor || DEFAULT_THEME} item={item} />
              </div>

              {index + 1 < filterResult.length && <hr className='my-1' />}
            </>
          ) : (
            <div className='px-2.5'>
              <Item primaryColor={primaryColor || DEFAULT_THEME} item={item} />
            </div>
          )}
        </React.Fragment>
      ))}

      {filterResult.length === 0 && <DisabledItem>{noOptionsMessage}</DisabledItem>}
    </div>
  );
};

export default Options;
